import { useState } from 'react';
import type {
  VenueDto,
  MewsCategoryDto,
  MewsCategoriesResponseDto,
  MewsMultiCategoryAllocationItemDto,
  GetMewsCategoriesRequest,
  GetMewsMultiCategoryAllocationRequest,
  MewsOccupancyDataDto,
  MewsProductDto,
  GetMewsBookingEngineProductsV1Params,
  CreateMewsReservationGroupRequest,
  CancelMewsReservationRequest,
  MewsReservationItemRequest,
  MewsCustomerRequest,
  ReservationGroupResponse,
  UserDto,
  ReservationDetailDto,
} from '@/services/platform/api/model';
import {
  getMewsVenuesV1,
  getMewsBookingEngineConfigurationV1,
  getMewsBookingEngineCategoriesV1,
  getMewsBookingEngineMultiCategoryAllocationV1,
  getMewsBookingEngineProductsV1,
  createMewsReservationGroupV1,
  cancelMewsReservationV1,
} from '../../../api/mews-reservations/mews-reservations';
import type {
  MewsAgeCategory,
  MewsAgeConfigDto,
  MewsAllocation,
  MewsBookingState,
  MewsCategoryItem,
  MewsHourSlot,
} from './types';
import { formatTime, splitName } from '@/lib/core/utils';
import type { TranslationKey } from '@/lib/core/i18n/types';
import { BookingState as ExperienceBookingState } from '@/components/shared/booking/engine/flows/experience';

// ── Re-export domain types ─────────────────────────────────────────────────────

export type { MewsAgeCategory, MewsAgeConfigDto };
export type {
  MewsCategory,
  MewsGuestDistributionEntry,
  MewsAllocation,
  MewsBookingState,
  MewsCategoryItem,
  MewsHourSlot,
  OpenSheet,
  StepperRowProps,
} from './types';

// ── Shared utilities ────────────────────────────────────────────────────────────

/**
 * Formats an age range as a human-readable string.
 *
 * - minimalAge only → "Ages X or above"
 * - maximalAge only → "Up to X years old"
 * - both            → "Ages X–Y"
 * - neither         → null
 */
export function formatAgeRange(minimalAge?: number | null, maximalAge?: number | null): string | null {
  if (minimalAge != null && maximalAge == null) return `Ages ${minimalAge} or above`;
  if (minimalAge == null && maximalAge != null) return `Up to ${maximalAge} years old`;
  if (minimalAge != null && maximalAge != null) return `Ages ${minimalAge}–${maximalAge}`;
  return null;
}

export function resolveProductName(product: MewsProductDto): string {
  return product.name ?? '';
}

export function resolveProductPrice(product: MewsProductDto): number {
  return product.prices?.['EUR'] ?? Object.values(product.prices ?? {})[0] ?? 0;
}

// ── Venues ─────────────────────────────────────────────────────────────────────

export async function getMewsVenues(
  locale?: string,
  serviceCategoryNodeKey?: string
): Promise<VenueDto[] | undefined> {
  try {
    const response = await getMewsVenuesV1(
      { serviceCategoryNodeKey },
      { headers: locale ? { 'Accept-Language': locale } : {} }
    );
    if (response.status === 200) return response.data;
    console.warn(`getMewsVenues: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getMewsVenues: request failed:', error);
    return undefined;
  }
}

// ── Booking Engine configuration ───────────────────────────────────────────────

export async function getMewsBookingEngineConfiguration(
  serviceId?: string,
  enterpriseId?: string
): Promise<MewsAgeConfigDto | undefined> {
  try {
    const params =
      serviceId || enterpriseId ? { ServiceId: serviceId, EnterpriseId: enterpriseId } : undefined;
    const response = await getMewsBookingEngineConfigurationV1(params);
    if (response.status === 200) return response.data;
    console.warn(`getMewsBookingEngineConfiguration: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getMewsBookingEngineConfiguration: request failed:', error);
    return undefined;
  }
}

/**
 * Whether the service's `when` step should offer a date **range** instead of a single date.
 *
 * Read from the Booking Engine configuration rather than assumed per flow. `timeUnit` is the length
 * of one bookable unit, so a `Day` (or `Month`) service spans several units and needs a from–to
 * selection, while an `Hour` service — Massages, for instance — is one slot on a single day.
 * `Additional` services are not bookable through these flows at all, so anything other than
 * `Bookable` is treated as single-date.
 *
 * Both fields are passed straight through from Mews as raw strings (see `MewsServiceDataDto`), so
 * neither is a closed union: they are compared case-insensitively, and an absent or unrecognised
 * `timeUnit` falls back to single-date. Mews documents `Day | Month | Hour` for the time unit and
 * `Bookable | Additional` for the discriminator in the Connector API; the Booking Engine docs list
 * fewer values than the API actually returns, so do not narrow this to a union type.
 */
export function mewsServiceUsesDateRange(config?: MewsAgeConfigDto): boolean {
  const data = config?.service?.data;
  if (data?.discriminator?.toLowerCase() !== 'bookable') return false;

  const timeUnit = data.timeUnit?.toLowerCase();
  return !!timeUnit && timeUnit !== 'hour';
}

/**
 * Whether the service is booked by the hour, and so needs an hour picked on top of the date.
 *
 * The mirror image of `mewsServiceUsesDateRange` rather than its negation: a service with no
 * configuration loaded yet, or one that is not `Bookable`, is neither a range nor a slot service,
 * so both helpers return false for it and the `when` step stays on a plain single date.
 */
export function mewsServiceUsesHourSlots(config?: MewsAgeConfigDto): boolean {
  const data = config?.service?.data;
  if (data?.discriminator?.toLowerCase() !== 'bookable') return false;

  return data.timeUnit?.toLowerCase() === 'hour';
}

/**
 * i18n key naming what one bookable unit of a service is called, e.g. a massage rather than a
 * sunbed. The key carries an ICU plural, so call sites pass `{ count }`.
 *
 * Keyed off the CMS `serviceType` because that is the only per-service word the app has: the Mews
 * service name ("Massages") is a catalogue title, not a countable noun. An unmapped service type
 * falls back to a neutral "unit" rather than borrowing another service's word — which is how the
 * experience flow ended up calling massages sunbeds.
 */
export function mewsUnitLabelKey(serviceType?: string | null): TranslationKey {
  switch (serviceType?.toLowerCase()) {
    case 'massage':
      return 'App.Labels.BookingUnitMassage';
    case 'tennis court':
      return 'App.Labels.BookingUnitTennisCourt';
    case 'bar':
      return 'App.Labels.BookingUnitBar';
    default:
      return 'App.Labels.BookingUnitFallback';
  }
}

/**
 * Whether a service is always booked for exactly one person.
 *
 * A massage is: the therapist takes one guest per slot, so the guest count is not a choice the
 * customer makes. Flows use this to fill the count in on selection and render the How many row
 * read-only, rather than offering a stepper whose only valid value is the one already there.
 *
 * Keyed off `serviceType` for the same reason as `mewsUnitLabelKey`: the Mews configuration has no
 * flag for it, and the age categories describe who may be counted, not how many may be.
 */
export function mewsServiceIsSingleOccupancy(serviceType?: string | null): boolean {
  return serviceType?.toLowerCase() === 'massage';
}

/**
 * i18n key for the period a price is quoted per, taken from the service's `timeUnit`.
 *
 * A one-hour massage priced "per massage/daily" is simply wrong: `averageAmountPerTimeUnit` is the
 * price of one time unit, so the label has to name the unit the service actually uses. Defaults to
 * daily, which is what every Mews service here was before hourly ones existed.
 */
export function mewsPricePeriodKey(config?: MewsAgeConfigDto): TranslationKey {
  switch (config?.service?.data?.timeUnit?.toLowerCase()) {
    case 'hour':
      return 'App.Labels.PricePeriodHourly';
    case 'month':
      return 'App.Labels.PricePeriodMonthly';
    default:
      return 'App.Labels.PricePeriodDaily';
  }
}

/**
 * Turns a categories response into the bookable hours of the queried day.
 *
 * `timeUnitStartsUtc` holds one entry per time unit in the requested range — for an `Hour` service
 * queried across a day, the 24 hourly starts — and every category's `availabilities` array is
 * positionally aligned with it. A slot is offered when *any* category still has a free unit in it,
 * because the category is only chosen in a later step; the category step then re-queries for the
 * chosen hour and filters itself.
 *
 * Labels are built from the UTC hour, so they read the same wherever the device happens to be.
 */
export function buildMewsHourSlots(response?: MewsCategoriesResponseDto): MewsHourSlot[] {
  const categories = response?.categories ?? [];

  return (response?.timeUnitStartsUtc ?? []).map((startUtc, index) => {
    const start = new Date(startUtc);
    const hour = start.getUTCHours();
    return {
      startUtc,
      startEpochMs: start.getTime(),
      hour,
      label: formatTime(`${String(hour).padStart(2, '0')}:00`),
      isAvailable: categories.some(category => (category.availabilities?.[index] ?? 0) > 0),
    };
  });
}

// ── Category mapping ───────────────────────────────────────────────────────────

/**
 * Maps a raw Mews category DTO to a `MewsCategoryItem`.
 * `priceUnit` is flow-specific: `'per sunbed/daily'` for sunbed, `'per room/nightly'` for stay.
 */
export function mapMewsCategoryDtoToItem(dto: MewsCategoryDto, priceUnit: string): MewsCategoryItem {
  const rateGroup = dto.prices?.occupancyPrices?.[0]?.rateGroupPrices?.[0];
  const minPrice = rateGroup?.minPrice?.averageAmountPerTimeUnit?.grossValue ?? 0;
  return {
    id: dto.id ?? '',
    name: dto.name as string,
    description: dto.description as string,
    pricePerDay: minPrice,
    maxCapacity: (dto.normalBedCount ?? 0) + (dto.extraBedCount ?? 0),
    maxPersons: `${(dto.normalBedCount ?? 0) + (dto.extraBedCount ?? 0)} persons`,
    availableUnits: dto.availabilities?.[0] ?? undefined,
    image: dto.imageAssignments?.[0]?.imageUrl ?? undefined,
    priceUnit,
    rateId: rateGroup?.minRateId,
  };
}

export function mapMewsAllocationItemToCategory(
  item: MewsMultiCategoryAllocationItemDto,
  priceUnit: string
): MewsCategoryItem {
  return mapMewsCategoryDtoToItem(item.category as MewsCategoryDto, priceUnit);
}

// ── Occupancy helpers ──────────────────────────────────────────────────────────

/**
 * Builds a minimal `MewsOccupancyDataDto[]` (minItems 1) from guest counts.
 * When `ageConfig` is available, maps adults/children to their age-category IDs.
 * Falls back to a single total-count entry (no category ID) when ageConfig is absent.
 */
function buildTotalOccupancy(
  adults: number,
  children: number,
  ageConfig?: MewsAgeConfigDto
): MewsOccupancyDataDto[] {
  const cats = ageConfig?.ageCategories ?? [];
  const adultCatId = cats.find(c => c.classification === 'Adult')?.id ?? cats[0]?.id;
  const childCatId = cats.find(c => c.classification === 'Child')?.id;

  const data: MewsOccupancyDataDto[] = [];
  if (adultCatId && adults > 0) data.push({ ageCategoryId: adultCatId, personCount: adults });
  if (childCatId && children > 0) data.push({ ageCategoryId: childCatId, personCount: children });

  // Fallback: ageConfig not yet loaded — total count without category ID satisfies @minItems 1
  if (data.length === 0) data.push({ personCount: Math.max(adults + children, 1) });

  return data;
}

// ── Shared allocation helpers ──────────────────────────────────────────────────

export function buildAllocationRequestItems(
  allocations: MewsAllocation[],
  ageConfig: MewsAgeConfigDto | undefined
) {
  const categories = ageConfig?.ageCategories ?? [];
  const adultCatId = categories.find(c => c.classification === 'Adult')?.id ?? categories[0]?.id;
  const childCatId = categories.find(c => c.classification === 'Child')?.id;

  return allocations.map(alloc => {
    const totalAdults = alloc.guestDistribution.reduce((s, g) => s + g.adults, 0);
    const totalChildren = alloc.guestDistribution.reduce((s, g) => s + g.children, 0);
    const totalGuests = totalAdults + totalChildren;

    const occupancyData: MewsOccupancyDataDto[] = [];
    if (adultCatId && totalAdults > 0)
      occupancyData.push({ ageCategoryId: adultCatId, personCount: totalAdults });
    if (childCatId && totalChildren > 0)
      occupancyData.push({ ageCategoryId: childCatId, personCount: totalChildren });
    if (occupancyData.length === 0 && adultCatId && totalGuests > 0)
      occupancyData.push({ ageCategoryId: adultCatId, personCount: totalGuests });
    // Fallback: no age config — total count without category ID satisfies @minItems 1
    if (occupancyData.length === 0) occupancyData.push({ personCount: Math.max(totalGuests, 1) });

    return { categoryId: alloc.categoryId, occupancyData };
  });
}

export function mergeAllocationPrices(
  allocations: MewsAllocation[],
  responseItems: MewsMultiCategoryAllocationItemDto[],
  ageConfig?: MewsAgeConfigDto
): MewsAllocation[] {
  const adultCatId = ageConfig?.ageCategories?.find(c => c.classification === 'Adult')?.id;
  const childCatId = ageConfig?.ageCategories?.find(c => c.classification === 'Child')?.id;

  return allocations.map((alloc, i) => {
    const responseItem = responseItems[i];
    if (!responseItem?.units?.length) return alloc;

    const guestDistribution = responseItem.units.map((unit, j) => {
      const pricePerDay =
        unit?.prices?.occupancyPrices?.[0]?.rateGroupPrices?.[0]?.minPrice?.averageAmountPerTimeUnit
          ?.grossValue;
      const adults = adultCatId
        ? (unit.occupancyData?.find(od => od.ageCategoryId === adultCatId)?.personCount ?? 0)
        : (alloc.guestDistribution[j]?.adults ?? 1);
      const children = childCatId
        ? (unit.occupancyData?.find(od => od.ageCategoryId === childCatId)?.personCount ?? 0)
        : (alloc.guestDistribution[j]?.children ?? 0);

      return { unitIndex: j, adults, children, ...(pricePerDay != null ? { pricePerDay } : {}) };
    });

    return {
      ...alloc,
      quantity: responseItem.units.length,
      guestDistribution,
      ...(responseItem.unitsAvailable != null ? { unitsAvailable: responseItem.unitsAvailable } : {}),
      ...(responseItem.unitsRequested != null ? { unitsRequested: responseItem.unitsRequested } : {}),
    };
  });
}

// ── Categories ─────────────────────────────────────────────────────────────────

interface UseMewsCategoriesOptions<T> {
  serviceId?: string;
  enterpriseId?: string;
  dateFrom: Date | null;
  dateTo: Date | null;
  adults: number;
  children: number;
  locale?: string;
  /** Passed to `buildTotalOccupancy`; when undefined the fallback total-count entry is used. */
  ageConfig?: MewsAgeConfigDto;
  /** Maps a raw DTO to the flow-specific category item shape. */
  mapDto: (dto: MewsCategoryDto) => T;
}

export function useMewsCategories<T>({
  serviceId,
  enterpriseId,
  dateFrom,
  dateTo,
  adults,
  children,
  locale,
  ageConfig,
  mapDto,
}: UseMewsCategoriesOptions<T>) {
  const [categories, setCategories] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetch = async () => {
    if (!dateFrom || !dateTo || !serviceId || !enterpriseId) return;
    const startUtc = dateFrom.toISOString();
    const endUtc = dateTo.toISOString();
    const occupancyData = buildTotalOccupancy(adults, children, ageConfig);
    setIsLoading(true);
    try {
      const response = await getMewsBookingEngineCategoriesV1(
        {
          serviceId,
          enterpriseId,
          startUtc,
          endUtc,
          currencyCode: 'EUR',
          occupancyData,
        },
        { headers: locale ? { 'Accept-Language': locale } : {} }
      );
      if (response.status === 200) {
        setCategories((response.data.categories ?? []).map(dto => mapDto(dto)));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { fetch, categories, isLoading };
}

export async function getMewsCategories(
  request: GetMewsCategoriesRequest,
  locale?: string
): Promise<MewsCategoriesResponseDto | undefined> {
  try {
    const response = await getMewsBookingEngineCategoriesV1(request, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });
    if (response.status === 200) return response.data;
    console.warn(`getMewsCategories: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getMewsCategories: request failed:', error);
    return undefined;
  }
}

export interface GetMewsHourSlotsOptions {
  serviceId: string;
  enterpriseId: string;
  /** Any instant on the day to list; only its calendar date is used. */
  day: Date;
  adults: number;
  children: number;
  ageConfig?: MewsAgeConfigDto;
  locale?: string;
}

/**
 * Lists the bookable hours of one day for an `Hour` service.
 *
 * Mews returns one time unit per entry of `timeUnitStartsUtc`, so the whole day is queried to get
 * all 24 hours back — the narrower range of a single chosen slot would return just that one.
 * Availability comes from the same response, which is why this is one request and not two.
 */
export async function getMewsHourSlots({
  serviceId,
  enterpriseId,
  day,
  adults,
  children,
  ageConfig,
  locale,
}: GetMewsHourSlotsOptions): Promise<MewsHourSlot[]> {
  const dayUtc = (hour: number) =>
    new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0)).toISOString();

  const response = await getMewsCategories(
    {
      serviceId,
      enterpriseId,
      startUtc: dayUtc(0),
      endUtc: dayUtc(23),
      currencyCode: 'EUR',
      occupancyData: buildTotalOccupancy(adults, children, ageConfig),
    },
    locale
  );

  return buildMewsHourSlots(response);
}

export async function getMewsMultiCategoryAllocation(
  request: GetMewsMultiCategoryAllocationRequest,
  locale?: string
) {
  try {
    const response = await getMewsBookingEngineMultiCategoryAllocationV1(request, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });
    if (response.status === 200) return response.data;
    console.warn(`getMewsMultiCategoryAllocation: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getMewsMultiCategoryAllocation: request failed:', error);
    return undefined;
  }
}

export async function getMewsProducts(
  params?: GetMewsBookingEngineProductsV1Params,
  locale?: string
): Promise<MewsProductDto[] | undefined> {
  try {
    const response = await getMewsBookingEngineProductsV1(
      params,
      locale ? { headers: { 'Accept-Language': locale } } : undefined
    );
    if (response.status === 200) return response.data;
    console.warn(`getMewsProducts: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getMewsProducts: request failed:', error);
    return undefined;
  }
}

// ── Multi-category allocation ──────────────────────────────────────────────────

interface UseMewsAllocationParams {
  serviceId?: string;
  enterpriseId?: string;
  dateFrom: Date | null;
  dateTo: Date | null;
  ageConfig?: MewsAgeConfigDto;
}

export function useMewsMultiCategoryAllocation(params: UseMewsAllocationParams) {
  const [isPending, setIsPending] = useState(false);

  const allocate = async (
    allocations: MewsAllocation[],
    onSuccess: (result: { allocations: MewsAllocation[] }) => void
  ) => {
    const { serviceId, enterpriseId, dateFrom, dateTo, ageConfig } = params;
    if (!dateFrom || !dateTo || !serviceId || !enterpriseId) return;

    const startUtc = dateFrom.toISOString();
    const endUtc = dateTo.toISOString();
    const items = buildAllocationRequestItems(allocations, ageConfig);

    setIsPending(true);
    try {
      const response = await getMewsBookingEngineMultiCategoryAllocationV1({
        serviceId,
        enterpriseId,
        startUtc,
        endUtc,
        currencyCode: 'EUR',
        rateIds: [],
        items,
      });
      if (response.status === 200) {
        const merged = mergeAllocationPrices(allocations, response.data.items ?? [], ageConfig);
        onSuccess({ allocations: merged });
      }
    } finally {
      setIsPending(false);
    }
  };

  return { allocate, isPending };
}

// ── Reservation group ──────────────────────────────────────────────────────────

function mapUserToCustomer(user: UserDto | null | undefined): MewsCustomerRequest {
  if (!user) return {};
  const { firstName, lastName } = splitName(user.name);
  return {
    email: user.email ?? null,
    firstName,
    lastName,
    telephone: user.phoneNumber ?? null,
    nationalityCode: null,
    sendMarketingEmails: false,
  };
}

interface BuildReservationGroupRequestOptions {
  ageConfig?: MewsAgeConfigDto;
  user?: UserDto | null;
}

export function buildMewsReservationGroupRequest(
  booking: MewsBookingState | ExperienceBookingState,
  { ageConfig, user }: BuildReservationGroupRequestOptions
): CreateMewsReservationGroupRequest {
  const cats = ageConfig?.ageCategories ?? [];
  const adultCatId = cats.find(c => c.classification === 'Adult')?.id ?? cats[0]?.id;
  const childCatId = cats.find(c => c.classification === 'Child')?.id;

  // These three fields are now required (non-nullable) on the backend model.
  // In practice they are always set by the time the user reaches step 3.
  const enterpriseId = booking.venue?.externalId ?? '';
  const venueNodeKey = booking.venue?.venueNodeKey ?? '';
  const serviceId = booking.venue?.bookableServiceExternalId ?? '';

  const startUtc = booking.dateFrom ? booking.dateFrom.toISOString() : undefined;
  let endUtc = booking.dateTo ? booking.dateTo.toISOString() : undefined;

  if (endUtc && (booking as ExperienceBookingState)?.room?.toLowerCase() == 'massages') {
    const d = new Date(endUtc);
    d.setHours(d.getHours() + 1);
    endUtc = d.toISOString();
  }

  const items: MewsReservationItemRequest[] = booking.allocations.map(alloc => {
    const distribution =
      alloc.guestDistribution.length > 0
        ? alloc.guestDistribution
        : [{ unitIndex: 0, adults: 1, children: 0 }];

    const units = distribution.map(guest => {
      const total = guest.adults + guest.children;
      const occupancyData: MewsOccupancyDataDto[] = [];
      if (adultCatId && guest.adults > 0)
        occupancyData.push({ ageCategoryId: adultCatId, personCount: guest.adults });
      if (childCatId && guest.children > 0)
        occupancyData.push({ ageCategoryId: childCatId, personCount: guest.children });
      if (occupancyData.length === 0 && adultCatId && total > 0)
        occupancyData.push({ ageCategoryId: adultCatId, personCount: total });
      // Fallback: no age config — total count without category ID satisfies @minItems 1
      if (occupancyData.length === 0) occupancyData.push({ personCount: Math.max(total, 1) });
      return {
        occupancyData,
        notes: booking.notes || undefined,
      };
    });

    return {
      categoryId: alloc.categoryId,
      productIds: booking.extras.length > 0 ? booking.extras : undefined,
      units,
    };
  });

  return {
    enterpriseId,
    venueNodeKey,
    serviceId,
    rateId: booking.category?.rateId,
    startUtc,
    endUtc,
    voucherCode: booking.voucherCode || undefined,
    customer: mapUserToCustomer(user),
    items,
  };
}

// Some backend error details wrap an upstream provider payload as `"Error <Verb>: {json}"` — the
// guest-facing text lives in that nested object's `Message` field, not in the outer detail string.
function extractReservationErrorMessage(detail?: string | null): string | undefined {
  if (!detail) return undefined;
  const jsonStart = detail.indexOf('{');
  if (jsonStart === -1) return detail;
  try {
    const parsed = JSON.parse(detail.slice(jsonStart));
    if (typeof parsed?.Message === 'string' && parsed.Message.trim()) return parsed.Message;
  } catch {
    // detail wasn't a wrapped JSON payload — fall back to the raw text below.
  }
  return detail;
}

export function useMewsCreateReservationGroup(locale: string) {
  const [isPending, setIsPending] = useState(false);

  const createReservationGroup = async (
    request: CreateMewsReservationGroupRequest,
    {
      onSuccess,
      onError,
    }: { onSuccess: (data: ReservationDetailDto) => void; onError?: (message?: string) => void }
  ) => {
    setIsPending(true);
    try {
      const response = await createMewsReservationGroupV1(request, {
        headers: { 'Accept-Language': locale },
      });
      if (response.status === 200) {
        onSuccess(response.data);
      } else {
        const detail = (response as { data?: { detail?: string } }).data?.detail;
        console.warn(`createReservationGroup: unexpected status ${response.status}`);
        onError?.(extractReservationErrorMessage(detail));
      }
    } catch (err) {
      console.warn('createReservationGroup: request failed:', err);
      onError?.();
    } finally {
      setIsPending(false);
    }
  };

  return { createReservationGroup, isPending };
}

// ── Cancel reservation ─────────────────────────────────────────────────────────

export async function cancelMewsReservation(
  request: CancelMewsReservationRequest
): Promise<boolean | undefined> {
  if (!request.enterpriseId) {
    console.warn('cancelMewsReservation: enterpriseId is required');
    return undefined;
  }
  if (!request.reservationIds?.length) {
    console.warn('cancelMewsReservation: reservationIds must have at least one entry');
    return undefined;
  }
  const payload: CancelMewsReservationRequest = {
    ...request,
    // notes is @minLength 1 on the backend; coerce empty string to a single space
    notes: request.notes?.trim() ? request.notes : ' ',
  };
  try {
    const response = await cancelMewsReservationV1(payload);
    if (response.status === 200) return true;
    console.warn(`cancelMewsReservation: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('cancelMewsReservation: request failed:', error);
    return undefined;
  }
}

// ── Re-exports ─────────────────────────────────────────────────────────────────

export type { VenueDto };
export type { MewsCategoryDto, MewsCategoriesResponseDto, GetMewsCategoriesRequest };
export type { MewsMultiCategoryAllocationItemDto, GetMewsMultiCategoryAllocationRequest };
export type { MewsProductDto, GetMewsBookingEngineProductsV1Params };
export type { CancelMewsReservationRequest };
export type { ReservationGroupResponse };
